import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Skeleton
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import Modal from "../../Components/Modal";
import makeRequest from "../../Requests/request";
import { useSnackbar } from "notistack";
import useGlobalContext from "../../Hooks/useGlobalContext";
import { users as usersRequest } from "../../Requests";

interface IManageEstablishmentUsersProps {
  establishmentId: number;
  open: boolean;
  close: () => void;
}

interface IUser {
  id: number;
  name: string;
  email: string;
}

const ManageEstablishmentUsers: React.FC<IManageEstablishmentUsersProps> = ({ establishmentId, open, close }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { loading, setLoading } = useGlobalContext();

  const [users, setUsers] = useState<IUser[]>([]);
  const [allUsers, setAllUsers] = useState<IUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const loadUsers = async () => {
    if (!establishmentId) return;
    setLoading?.(true);
    try {
      const request = makeRequest('establishments');
      const { data } = await request.get(`${establishmentId}/users`);
      const listRaw: any[] = Array.isArray(data) ? data : [];

      const parsed: IUser[] = listRaw.map((u: any) => ({
        id: u.id ?? u.identifier,
        name: u.name,
        email: u.email
      }));
      setUsers(parsed);
    } catch (e) {
      enqueueSnackbar('Erro ao carregar usuários.', { variant: 'error' });
    }
    setLoading?.(false);
  };

  const loadAllUsers = async () => {
    try {
      const resultUsers = await usersRequest.get({ page: 1, recordsPerPage: 10000 });
      const usersArray: any[] = Array.isArray(resultUsers?.data)
        ? resultUsers.data
        : Array.isArray(resultUsers?.data?.data)
          ? resultUsers.data.data
          : [];

      const parsedUsers: IUser[] = usersArray.map((u: any) => ({
        id: u.id ?? u.identifier,
        name: u.name,
        email: u.email
      }));
      setAllUsers(parsedUsers);
    } catch (e) {
      enqueueSnackbar('Erro ao carregar lista de usuários.', { variant: 'error' });
    }
  };

  const handleAddUser = async () => {
    if (!selectedUserId) return;
    setLoading?.(true);
    try {
      const request = makeRequest('establishments');
      await request.post(`${establishmentId}/users`, { user_id: selectedUserId });
      enqueueSnackbar('Usuário adicionado com sucesso!', { variant: 'success' });
      setSelectedUserId(null);
      await loadUsers();
    } catch (e) {
      enqueueSnackbar('Erro ao adicionar usuário.', { variant: 'error' });
    }
    setLoading?.(false);
  };

  const handleRemoveUser = (userId: number) => async () => {
    setLoading?.(true);
    try {
      const request = makeRequest('establishments');
      await request.delete(`${establishmentId}/users/${userId}`);
      enqueueSnackbar('Usuário removido com sucesso!', { variant: 'success' });
      await loadUsers();
    } catch (e) {
      enqueueSnackbar('Erro ao remover usuário.', { variant: 'error' });
    }
    setLoading?.(false);
  };

  useEffect(() => {
    if (open) {
      loadUsers();
      loadAllUsers();
    }
  }, [open]);

  return (
    <Modal
      title="Usuários vinculados ao estabelecimento"
      open={open}
      closeModal={close}
      maxWidth="sm"
    >
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
          <Skeleton variant="circular" width={40} height={40} />
        </Stack>
      ) : (
        <Grid container spacing={0.5}>
          <Grid item xs={12}>
            {users.length === 0 ? (
              <Typography>Nenhum usuário vinculado.</Typography>
            ) : (
              <List>
                {users.map((user) => (
                  <ListItem key={user.id} secondaryAction={
                    <Button onClick={handleRemoveUser(user.id)} aria-label="Remover usuário" color="error" size="small" startIcon={<DeleteForeverIcon />}>
                      Remover
                    </Button>
                  }>
                    <GroupIcon sx={{ mr: 1 }} />
                    <ListItemText primary={user.name} secondary={user.email} />
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} alignItems="center">
              <select
                value={selectedUserId ?? ""}
                onChange={(e) => setSelectedUserId(Number(e.target.value) || null)}
                style={{ flexGrow: 1, padding: 8 }}
              >
                <option value="">Selecione um usuário</option>
                {allUsers
                  .filter((u) => !users.find((v) => v.id === u.id))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
              </select>
              <Button
                variant="contained"
                onClick={handleAddUser}
                disabled={!selectedUserId}
                startIcon={<PersonAddAlt1Icon />}
                aria-label="Adicionar usuário"
              >
                Adicionar
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="outlined" onClick={close} aria-label="Fechar modal">
                Fechar
              </Button>
            </Stack>
          </Grid>
        </Grid>
      )}
    </Modal>
  );
};

export default ManageEstablishmentUsers; 